/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars, eqeqeq, no-lone-blocks */
import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLanguageCode } from '../utils/rtlUtils';
import { useLanguages } from '../hooks/useLanguages';
import { fetchTranslations } from '../redux/userSlice';

// Create RTL Context
// NOTE: This context is kept for future use but currently NOT setting global dir attribute
// RTL is only applied to specific process pages (Map/Swimlane views)
const RTLContext = createContext({
  direction: 'ltr',
  languageCode: '',
});

// RTL Provider Component
export const RTLProvider = ({ children }) => {
  const user = useSelector((state) => state.user.user);
  const { languages, loading: languagesLoading } = useLanguages();
  const [direction, setDirection] = useState('ltr');
  const [languageCode, setLanguageCode] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);

  const translations = useSelector((state) => state.user.translations);
  const currentLang = useSelector((state) => state.user.lang);
  const dispatch = useDispatch();
  const fetchedLangRef = useRef(null);

  useEffect(() => {
    if (languagesLoading) return;

    const initialize = async () => {
      let code = 'EN'; // default fallback is EN (uppercase)
      if (user && user.language_id && languages.length > 0) {
        // Logged-in user: ALWAYS prioritize user.language_id for UI translations
        const mappedCode = getLanguageCode(user.language_id, languages);
        if (mappedCode) {
          code = mappedCode.toUpperCase();
        }
      } else {
        // Public site / Not logged in: Default to system (browser language or EN)
        const browserLang = navigator.language ? navigator.language.split('-')[0] : 'en';
        code = (browserLang || 'en').toUpperCase();
      }

      const rtl = false;
      const dir = 'ltr';

      setDirection(dir);
      setLanguageCode(code);

      try {
        document.documentElement.setAttribute('dir', 'ltr');
        document.body.setAttribute('dir', 'ltr');
        localStorage.setItem('appDirection', 'ltr');
      } catch (e) {}

      // Fetch translations if empty or language code changed
      const hasTranslations = Object.keys(translations || {}).length > 0;
      const reduxLangMismatch = (currentLang || '').toUpperCase() !== code;

      if (
        (fetchedLangRef.current !== code && (!hasTranslations || reduxLangMismatch)) ||
        !hasTranslations
      ) {
        fetchedLangRef.current = code;
        dispatch(fetchTranslations(code));
      } else if (fetchedLangRef.current === null) {
        fetchedLangRef.current = code;
      }
      setIsLoaded(true);
    };

    initialize();
  }, [user?.language_id, languages, languagesLoading, dispatch, currentLang, translations, user]);

  const value = {
    direction,
    languageCode,
  };

  if (!isLoaded) return null; // 🚀 Prevent flash of keys

  return <RTLContext.Provider value={value}>{children}</RTLContext.Provider>;
};

// Custom hook to use RTL context
export const useRTL = () => {
  const context = useContext(RTLContext);
  if (!context) {
    throw new Error('useRTL must be used within RTLProvider');
  }
  return context;
};

export default RTLContext;
